// packages/web/src/api/account.ts
/**
 * Account API client — balance + credits history.
 * Like auth.ts, uses credentials: 'include' for the session cookie.
 */

import { INGEST_URL, IngestApiError, accountHeaders } from '../lib/api-client.js';

export interface BalanceResponse {
  balance: number;
  lowBalanceThreshold: number;
  notifyEmail: boolean;
  notifyPush: boolean;
  /** 19BO+ Line 2 B4.4 step 4: progress-cloud-sync opt-in. */
  progressCloudSync: boolean;
  /** 19BO+ B5.2-cron: auto-diagnose opt-in (cron work-list 读这个). */
  autoDiagnoseEnabled: boolean;
  /** 19BO+ B5.2-cron: cron 诊断间隔（小时），受控枚举 6/12/24. */
  autoDiagnoseIntervalHours: number;
}

export interface CreditAdjustmentItem {
  id: string;
  delta: number;
  balanceAfter: number;
  reason: string;
  refId: string | null;
  createdAt: number;
}

export interface CreditsHistoryResponse {
  items: CreditAdjustmentItem[];
  page: number;
  pageSize: number;
  total: number;
}

async function getAccount<T>(path: string): Promise<T | null> {
  const res = await fetch(`${INGEST_URL}${path}`, {
    method: 'GET',
    credentials: 'include',
  });
  if (res.status === 401) return null;
  if (!res.ok) {
    const json = await res.json().catch(() => ({ error: 'unknown' }));
    throw new IngestApiError(res.status, json);
  }
  return res.json();
}

export interface PreferencesPatch {
  lowBalanceThreshold?: number;
  notifyEmail?: boolean;
  notifyPush?: boolean;
  /** 19BO+ Line 2 B4.4 step 4: progress-cloud-sync opt-in. */
  progressCloudSync?: boolean;
  /** 19BO+ B5.2-cron: auto-diagnose opt-in toggle. */
  autoDiagnoseEnabled?: boolean;
  /** 19BO+ B5.2-cron: cron 诊断间隔（小时），受控枚举 6/12/24. */
  autoDiagnoseIntervalHours?: number;
}

export const accountApi = {
  getBalance: () => getAccount<BalanceResponse>('/account/balance'),
  getCreditsHistory: (page = 1) =>
    getAccount<CreditsHistoryResponse>(`/account/credits/history?page=${page}`),
  /**
   * 19BN Phase D D1/D4: update low-balance threshold + notify channels.
   * Throws IngestApiError on failure. The caller (account-slice /
   * settings panel) optimistically updates local state and reverts on throw.
   */
  updatePreferences: async (patch: PreferencesPatch): Promise<{ ok: true }> => {
    const res = await fetch(`${INGEST_URL}/account/preferences`, {
      method: 'PATCH',
      headers: accountHeaders(),
      credentials: 'include',
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const json = await res.json().catch(() => ({ error: 'unknown' }));
      throw new IngestApiError(res.status, json);
    }
    return res.json();
  },
};
